import type { EventMapResponse, MapVariant } from "@mvk/shared";
import type { Company, Hall } from "../types";
import { isApiEnabled, apiConfig } from "../api/config";
import { adminAuthStorage, visitorAuthStorage } from "../api/authStorage";
import { mapApi } from "../api/map";
import { withMapLayout } from "./mapLayout";
import { debugLog } from "./debugLog";
import { eventPackService } from "./eventPackService";

function boothToCompany(booth: EventMapResponse["booths"][number]): Company {
  return {
    id: booth.companyId,
    eventId: booth.eventId,
    name: booth.name,
    category: booth.category,
    tags: [],
    hall: booth.hall,
    booth: booth.booth,
    shortAbout: booth.shortAbout,
    products: [],
    contact: {},
    qrCodeValue: `company:${booth.companyId}`,
    logoUrl: booth.logoUrl,
    pinColor: booth.pinColor,
    mapLocked: booth.mapLocked === true,
    mapLabelRot: booth.mapLabelRot ?? 0,
    mapLabel: booth.mapLabel == null ? null : booth.mapLabel,
    mapLabelLines:
      booth.mapLabelLines === 1 ? 1 : booth.mapLabelLines === 3 ? 3 : 2,
    mapLabelScale:
      typeof booth.mapLabelScale === "number" ? booth.mapLabelScale : null,
    mapPoint: {
      x: booth.mapPoint.x,
      y: booth.mapPoint.y,
      ...(booth.mapPoint.w != null ? { w: booth.mapPoint.w } : {}),
      ...(booth.mapPoint.h != null ? { h: booth.mapPoint.h } : {}),
      hallId: booth.mapPoint.hallId ?? booth.hallId ?? "",
    },
  };
}

function hallDtoToHall(hall: EventMapResponse["halls"][number]): Hall {
  return {
    id: hall.id,
    eventId: hall.eventId,
    name: hall.name,
    label: hall.label,
    floor: hall.floor,
    floorplanScene: hall.floorplanScene,
    floorplanSvg: hall.floorplanSvg,
    floorplanWidth: hall.floorplanWidth,
    floorplanHeight: hall.floorplanHeight,
    crop: hall.crop,
  };
}

export interface MapViewData {
  companies: Company[];
  halls: Hall[];
  mapVariant: MapVariant;
  /** Diagnostics from last fetch (for on-screen debug). */
  debug?: MapFetchDebug;
}

export type MapFetchDebug = {
  path: string;
  status: number;
  contentLengthHeader: string | null;
  rawTextBytes: number;
  parseOk: boolean;
  parseError?: string;
  halls: Array<{
    id: string;
    label: string;
    svgLen: number;
    svgHead: string;
    w?: number;
    h?: number;
  }>;
  booths: number;
  boothsWithCoords: number;
};

const MAP_CACHE_TTL_MS = 60_000;
const mapCache = new Map<string, { at: number; data: MapViewData }>();

function mapCacheKey(eventId: string, audience: "visitor" | "admin"): string {
  return `${audience}:${eventId}`;
}

function isAuthenticated(audience: "visitor" | "admin") {
  return audience === "admin"
    ? adminAuthStorage.isAuthenticated()
    : visitorAuthStorage.isAuthenticated();
}

/** Fetch map with raw-body size logging (doesn't rely on DevTools Size column). */
async function fetchMapRaw(
  eventId: string,
  audience: "visitor" | "admin",
  includeLogos = false,
): Promise<{ data: EventMapResponse; debug: MapFetchDebug }> {
  const path = `/events/${eventId}/map${includeLogos ? "?includeLogos=1" : ""}`;
  const storage = audience === "admin" ? adminAuthStorage : visitorAuthStorage;
  const token = storage.getToken();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${apiConfig.baseUrl}${path}`, { headers });
  const contentLengthHeader = res.headers.get("content-length");
  const text = await res.text();
  const rawTextBytes = new TextEncoder().encode(text).length;

  const baseDebug: MapFetchDebug = {
    path,
    status: res.status,
    contentLengthHeader,
    rawTextBytes,
    parseOk: false,
    halls: [],
    booths: 0,
    boothsWithCoords: 0,
  };

  if (!res.ok) {
    debugLog.error("map", `fetch failed ${res.status}`, { ...baseDebug, bodyHead: text.slice(0, 200) });
    throw new Error(`map HTTP ${res.status}: ${text.slice(0, 120)}`);
  }

  let data: EventMapResponse;
  try {
    data = JSON.parse(text) as EventMapResponse;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    debugLog.error("map", "JSON.parse failed", { ...baseDebug, parseError: msg, bodyHead: text.slice(0, 200) });
    throw err;
  }

  const halls = (data.halls ?? []).map((h) => ({
    id: h.id,
    label: h.label,
    svgLen: h.floorplanSvg?.length ?? 0,
    svgHead: (h.floorplanSvg ?? "").slice(0, 80),
    w: h.floorplanWidth,
    h: h.floorplanHeight,
  }));
  const booths = data.booths ?? [];
  const debug: MapFetchDebug = {
    ...baseDebug,
    parseOk: true,
    halls,
    booths: booths.length,
    boothsWithCoords: booths.filter((b) => (b.mapPoint?.x ?? 0) > 0 && (b.mapPoint?.y ?? 0) > 0).length,
  };

  debugLog.info("map", "raw fetch ok", {
    status: debug.status,
    contentLengthHeader: debug.contentLengthHeader,
    rawTextBytes: debug.rawTextBytes,
    halls: debug.halls.map((h) => ({ id: h.id, svgLen: h.svgLen, w: h.w, h: h.h })),
    booths: debug.booths,
    boothsWithCoords: debug.boothsWithCoords,
  });

  return { data, debug };
}

export const mapService = {
  async get(eventId: string, audience: "visitor" | "admin" = "visitor"): Promise<MapViewData> {
    const cacheKey = mapCacheKey(eventId, audience);
    const hit = mapCache.get(cacheKey);
    if (hit && Date.now() - hit.at < MAP_CACHE_TTL_MS) {
      return hit.data;
    }

    const buildFromMap = (data: EventMapResponse, debug?: MapFetchDebug): MapViewData => {
      const halls = data.halls.map(hallDtoToHall);
      let companies = data.booths.map(boothToCompany);
      // Visitor + real floorplan: never invent coords for unplaced exhibitors
      // (old withMapLayout grid + proximity snap stacked e.g. A507 under A519).
      // Fake grid only for legacy events with no SVG/scene plan.
      if (audience === "visitor") {
        const hasRealPlan = halls.some(
          (h) =>
            Boolean(h.floorplanSvg && h.floorplanSvg.includes("<svg")) ||
            Boolean(h.floorplanScene && (h.floorplanScene.objects?.length ?? 0) > 0),
        );
        if (!hasRealPlan) {
          const needsLayout = companies.some((c) => c.mapPoint.x <= 0 || c.mapPoint.y <= 0);
          if (needsLayout) {
            companies = withMapLayout(companies, halls);
          }
        }
      }
      return { companies, halls, mapVariant: data.mapVariant, debug };
    };

    const remember = (view: MapViewData): MapViewData => {
      mapCache.set(cacheKey, { at: Date.now(), data: view });
      return view;
    };

    if (audience === "visitor") {
      const pack = await eventPackService.getCached(eventId);
      if (pack?.map) {
        if (isApiEnabled() && isAuthenticated(audience)) {
          try {
            // Visitor map: skip data: logos (API default). Admin passes includeLogos below.
            const { data, debug } = await fetchMapRaw(eventId, audience, false);
            return remember(buildFromMap(data, debug));
          } catch {
            return remember(buildFromMap(pack.map));
          }
        }
        return remember(buildFromMap(pack.map));
      }
    }

    if (!isApiEnabled() || !isAuthenticated(audience)) {
      throw new Error("[mapService] API auth required");
    }

    const { data, debug } = await fetchMapRaw(eventId, audience, audience === "admin");
    return remember(buildFromMap(data, debug));
  },

  /** Drop cached map after admin edits so the next list/map sees fresh booths. */
  invalidate(eventId?: string) {
    if (!eventId) {
      mapCache.clear();
      return;
    }
    mapCache.delete(mapCacheKey(eventId, "visitor"));
    mapCache.delete(mapCacheKey(eventId, "admin"));
  },

  async updateBooth(
    eventId: string,
    companyId: string,
    input: {
      booth?: string;
      hall?: string;
      hallId?: string;
      mapX?: number | null;
      mapY?: number | null;
      mapW?: number | null;
      mapH?: number | null;
      pinColor?: string | null;
      mapLocked?: boolean;
      mapLabelRot?: 0 | 90 | 180 | 270;
      mapLabel?: string | null;
      mapLabelLines?: 1 | 2 | 3;
      mapLabelScale?: number | null;
    },
  ): Promise<Company> {
    if (!isApiEnabled() || !adminAuthStorage.isAuthenticated()) {
      throw new Error("[mapService] admin API required");
    }
    const { booth } = await mapApi.updateBooth(eventId, companyId, input, "admin");
    mapService.invalidate(eventId);
    return boothToCompany(booth);
  },

  async updateBoothPin(
    eventId: string,
    companyId: string,
    mapX: number,
    mapY: number,
  ): Promise<void> {
    await mapService.updateBooth(eventId, companyId, { mapX, mapY });
  },

  async uploadFloorplan(eventId: string, hallId: string, floorplanSvg: string) {
    if (!isApiEnabled() || !adminAuthStorage.isAuthenticated()) {
      throw new Error("[mapService] admin API required");
    }
    const { hall } = await mapApi.uploadFloorplan(eventId, hallId, floorplanSvg);
    return hallDtoToHall(hall);
  },

  async uploadFloorplanFile(eventId: string, hallId: string, file: File) {
    if (!isApiEnabled() || !adminAuthStorage.isAuthenticated()) {
      throw new Error("[mapService] admin API required");
    }
    const res = await mapApi.uploadFloorplanFile(eventId, hallId, file);
    return {
      hall: hallDtoToHall(res.hall),
      source: res.source,
      boothCodes: res.boothCodes ?? [],
      converter: res.converter,
    };
  },

  async updateFloorplanBoard(
    eventId: string,
    hallId: string,
    boardWidth: number,
    boardHeight: number,
  ) {
    if (!isApiEnabled() || !adminAuthStorage.isAuthenticated()) {
      throw new Error("[mapService] admin API required");
    }
    const { hall } = await mapApi.updateFloorplanBoard(eventId, hallId, boardWidth, boardHeight);
    return hallDtoToHall(hall);
  },

  async updateFloorplanCrop(
    eventId: string,
    hallId: string,
    crop: { left: number; top: number; right: number; bottom: number },
  ) {
    if (!isApiEnabled() || !adminAuthStorage.isAuthenticated()) {
      throw new Error("[mapService] admin API required");
    }
    const { hall } = await mapApi.updateFloorplanCrop(eventId, hallId, crop);
    return hallDtoToHall(hall);
  },

  async rotateFloorplan(
    eventId: string,
    hallId: string,
    quarterTurns: 1 | -1 | 2,
    visualOnly = true,
  ) {
    if (!isApiEnabled() || !adminAuthStorage.isAuthenticated()) {
      throw new Error("[mapService] admin API required");
    }
    const { hall } = await mapApi.rotateFloorplan(eventId, hallId, quarterTurns, visualOnly);
    mapService.invalidate(eventId);
    return hallDtoToHall(hall);
  },

  async fitFloorplan(eventId: string, hallId: string) {
    if (!isApiEnabled() || !adminAuthStorage.isAuthenticated()) {
      throw new Error("[mapService] admin API required");
    }
    const { hall } = await mapApi.fitFloorplan(eventId, hallId);
    mapService.invalidate(eventId);
    return hallDtoToHall(hall);
  },

  async clearFloorplan(eventId: string, hallId: string) {
    if (!isApiEnabled() || !adminAuthStorage.isAuthenticated()) {
      throw new Error("[mapService] admin API required");
    }
    const { hall } = await mapApi.clearFloorplan(eventId, hallId);
    mapService.invalidate(eventId);
    return hallDtoToHall(hall);
  },

  async updateFloorplanScene(
    eventId: string,
    hallId: string,
    scene: import("@mvk/shared").FloorplanScene,
  ) {
    if (!isApiEnabled() || !adminAuthStorage.isAuthenticated()) {
      throw new Error("[mapService] admin API required");
    }
    const { hall } = await mapApi.updateFloorplanScene(eventId, hallId, scene);
    mapService.invalidate(eventId);
    return hallDtoToHall(hall);
  },
};
